import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CATEGORIEEN, type Verbeterpunt } from "../../lib/verbeterlus";
import { verbeterApi } from "../../lib/verbeterlus-api";

export function MeldVerbeterpunt({
  kanaal,
  bronId,
}: {
  kanaal: "mail" | "voice";
  bronId: string;
}) {
  const [enabled, setEnabled] = useState(false),
    [open, setOpen] = useState(false),
    [busy, setBusy] = useState(false);
  const [titel, setTitel] = useState(""),
    [verwachting, setVerwachting] = useState(""),
    [categorie, setCategorie] = useState("actie_ontbreekt");
  const [punten, setPunten] = useState<Verbeterpunt[]>([]),
    [punt, setPunt] = useState(""),
    [id, setId] = useState(() => crypto.randomUUID());
  const [error, setError] = useState(""),
    [saved, setSaved] = useState("");
  const [terugkoppeling, setTerugkoppeling] = useState("");
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    verbeterApi(`?bron_id=${bronId}&kanaal=${kanaal}`)
      .then((d) => {
        const m = d.meldingen?.[0];
        if (active && m) {
          setSaved(m.verbeterpunt_id);
          setTerugkoppeling(
            `${m.punt?.status || "gemeld"} · klantgeval ${m.klant_status}${m.punt?.terugkoppeling ? " · " + m.punt.terugkoppeling : ""}`,
          );
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [enabled, bronId, kanaal]);
  useEffect(() => {
    let active = true;
    verbeterApi("?config=1")
      .then((x) => {
        if (active) setEnabled(x.enabled);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    setOpen(false);
    setSaved("");
    setId(crypto.randomUUID());
    setTitel("");
    setVerwachting("");
    setPunt("");
    setError("");
  }, [bronId]);
  async function begin() {
    setOpen(true);
    setError("");
    try {
      const d = await verbeterApi("?open=1&zoek=" + encodeURIComponent(titel));
      setPunten(d.punten);
    } catch (e) {
      setError(String(e));
    }
  }
  async function save() {
    setBusy(true);
    setError("");
    try {
      const d = await verbeterApi("", {
        id,
        kanaal,
        bron_id: bronId,
        titel,
        categorie,
        verwachting,
        punt_id: punt || null,
      });
      setSaved(d.id);
      setId(crypto.randomUUID());
      setTerugkoppeling("Melding opgeslagen; nog niet uitgevoerd.");
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Melden mislukt");
    } finally {
      setBusy(false);
    }
  }
  if (!enabled) return null;
  return (
    <span className="text-sm">
      {saved && (
        <Link
          className="text-indigo-700 underline"
          to={`/agent-verbeteringen?id=${saved}`}
        >
          Melding volgen: {terugkoppeling}
        </Link>
      )}
      <button
        type="button"
        className="text-indigo-700 underline ml-2"
        onClick={() => void begin()}
      >
        Actie ontbreekt / fout melden
      </button>
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Verbeterpunt melden"
          className="fixed inset-0 z-[100] bg-black/30 flex items-center justify-center p-4"
          onKeyDown={(e) => {
            if (e.key === "Escape" && !busy) setOpen(false);
          }}
        >
          <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-lg space-y-3 text-slate-800">
            <h2 className="font-semibold">Wat had de agent moeten doen?</h2>
            <p className="text-xs">
              We koppelen dit bericht of gesprek automatisch. Melden voert geen
              klantactie uit.
            </p>
            <label className="block">
              Soort
              <select
                className="block border rounded p-2 w-full"
                value={categorie}
                onChange={(e) => setCategorie(e.target.value)}
              >
                {Object.entries(CATEGORIEEN).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              Korte titel
              <input
                className="block border rounded p-2 w-full"
                maxLength={200}
                value={titel}
                onChange={(e) => setTitel(e.target.value)}
              />
            </label>
            <label className="block">
              Verwachte handeling
              <textarea
                className="block border rounded p-2 w-full"
                maxLength={3000}
                rows={4}
                value={verwachting}
                onChange={(e) => setVerwachting(e.target.value)}
              />
            </label>
            <label className="block">
              Bundelen bij bestaand punt (optioneel)
              <select
                className="block border rounded p-2 w-full"
                value={punt}
                onChange={(e) => setPunt(e.target.value)}
              >
                <option value="">Nieuw verbeterpunt</option>
                {punten.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.titel}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="underline"
              disabled={busy}
              onClick={() => void begin()}
            >
              Zoek bestaande punten met deze titel
            </button>
            {error && (
              <p role="alert" className="text-red-700">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button disabled={busy} onClick={() => setOpen(false)}>
                Sluiten
              </button>
              <button
                className="bg-indigo-600 text-white rounded px-3 py-2 disabled:opacity-50"
                disabled={
                  busy ||
                  titel.trim().length < 3 ||
                  verwachting.trim().length < 3
                }
                onClick={() => void save()}
              >
                {busy ? "Opslaan…" : "Melding vastleggen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </span>
  );
}
